import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { newsQueryOptions, type NewsArticle } from "@/lib/news.queries";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { FramedPicture } from "@/components/site/FramedPicture";
import { noWidow } from "@/lib/utils";

export const Route = createFileRoute("/news")({
  head: () => ({
    meta: [
      { title: "Aesthetic Insights — Medical Insight" },
      {
        name: "description",
        content:
          "Aesthetic Insights — the Aesthetic Industry Association newsletter covering medical aesthetics, devices, injectables, and market trends.",
      },
      { property: "og:title", content: "Aesthetic Insights — Medical Insight" },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(newsQueryOptions());
  },
  component: NewsPage,
});

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Archive pagination: render ~20 at a time, revealed client-side (the query
// already holds the full list). 21 keeps the 3-column grid's rows full.
const PAGE = 21;

/**
 * List-state restoration (filters + "Load more" depth), so reading an article
 * and coming BACK never loses the reader's place.
 *
 * The saved state is keyed by the history entry's own key (the same per-entry
 * key the router's built-in scrollRestoration uses). A back/forward navigation
 * revisits the SAME history entry → same key → state restores; a fresh click
 * on the nav creates a NEW entry → new key → clean defaults. sessionStorage
 * scopes it to the tab and lets it die with the tab — the right lifetime for
 * "don't lose my spot".
 *
 * Scroll itself needs no code here: state restores synchronously in the
 * useState initializers, so the first paint already has the full expanded list
 * height (cards have fixed aspect ratios, so height doesn't depend on image
 * loading), and the router's native scrollRestoration lands exactly where the
 * reader left. Zero extra network or database traffic — everything runs on the
 * one cached list query (see newsQueryOptions staleTime/gcTime).
 */
type NewsListState = { q: string; year: string; month: string; visibleCount: number };

function loadListState(key: string): NewsListState | null {
  try {
    const raw = sessionStorage.getItem(`news-list:${key}`);
    if (!raw) return null;
    const s = JSON.parse(raw) as NewsListState;
    if (typeof s !== "object" || s === null) return null;
    return {
      q: typeof s.q === "string" ? s.q : "",
      year: typeof s.year === "string" ? s.year : "all",
      month: typeof s.month === "string" ? s.month : "all",
      visibleCount:
        Number.isFinite(s.visibleCount) && s.visibleCount > 0 ? s.visibleCount : PAGE,
    };
  } catch {
    return null;
  }
}

function saveListState(key: string, s: NewsListState) {
  try {
    sessionStorage.setItem(`news-list:${key}`, JSON.stringify(s));
  } catch {
    /* storage unavailable — restoration is a convenience, never an error */
  }
}

function NewsPage() {
  return (
    <Suspense
      fallback={
        <div className="mx-auto max-w-7xl px-4 py-16 text-sm text-muted-foreground">
          Loading Aesthetic Insights…
        </div>
      }
    >
      <NewsContent />
    </Suspense>
  );
}

function NewsContent() {
  const { data: all } = useSuspenseQuery(newsQueryOptions());
  // Stable per-history-entry key (same source the router's scrollRestoration
  // uses): back/forward revisits reuse it, fresh navigations get a new one.
  const locKey = useLocation({
    select: (l) => l.state.__TSR_key ?? l.state.key ?? "static",
  });
  const restored = useRef<NewsListState | null | undefined>(undefined);
  if (restored.current === undefined) restored.current = loadListState(locKey);

  const [q, setQ] = useState(restored.current?.q ?? "");
  const [year, setYear] = useState<string>(restored.current?.year ?? "all");
  const [month, setMonth] = useState<string>(restored.current?.month ?? "all");

  const years = useMemo(() => {
    const ys = new Set<number>();
    all.forEach((a) => ys.add(new Date(a.published_at).getUTCFullYear()));
    return Array.from(ys).sort((a, b) => b - a);
  }, [all]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((a) => {
      const d = new Date(a.published_at);
      const y = d.getUTCFullYear();
      const m = d.getUTCMonth() + 1;
      if (year !== "all" && String(y) !== year) return false;
      if (month !== "all" && String(m) !== month) return false;
      if (needle) {
        const hay = `${a.title} ${a.excerpt ?? ""} ${a.category ?? ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [all, q, year, month]);

  const [feature, ...rest] = filtered;
  const secondary = rest.slice(0, 4);
  const remaining = rest.slice(4);

  const [visibleCount, setVisibleCount] = useState(restored.current?.visibleCount ?? PAGE);
  // New search/filter view starts back at the first page — but only on a real
  // filter CHANGE, never on mount (which would clobber a restored depth).
  const prevFilters = useRef({ q, year, month });
  useEffect(() => {
    const p = prevFilters.current;
    if (p.q !== q || p.year !== year || p.month !== month) {
      prevFilters.current = { q, year, month };
      setVisibleCount(PAGE);
    }
  }, [q, year, month]);
  // Persist the list state under this history entry's key on every change.
  useEffect(() => {
    saveListState(locKey, { q, year, month, visibleCount });
  }, [locKey, q, year, month, visibleCount]);
  const visible = remaining.slice(0, visibleCount);

  return (
    <div className="bg-background">
      {/* Masthead */}
      <PageHero
        page="insights"
        kicker="Aesthetic Industry Association"
        title="Aesthetic Insights"
        titleFontClass="font-display"
        subtitle={
          <>
            Industry news, market intelligence, and meeting highlights from
            Medical Insight — published throughout the year for
            executives, clinicians, and investors in medical{"\u00A0"}aesthetics.
          </>
        }
      />

      {/* Filter bar */}
      <section className="sticky top-[52px] z-30 border-b bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
          <Input
            placeholder="Search headlines, topics, companies…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="sm:max-w-md"
          />
          <div className="flex items-center gap-2 sm:ml-auto">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTHS.map((label, i) => (
                  <SelectItem key={label} value={String(i + 1)}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(q || year !== "all" || month !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQ("");
                  setYear("all");
                  setMonth("all");
                }}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-10 sm:py-14">
        {filtered.length === 0 ? (
          <div className="rounded-lg border bg-card py-20 text-center text-sm text-muted-foreground">
            No articles match your filters.
          </div>
        ) : (
          <>
            {/* Feature + the next four stories in the right column */}
            {feature && (
              <section className="mb-12 grid gap-6 lg:grid-cols-3 lg:gap-8">
                <FeatureCard article={feature} />
                <div className="flex flex-col gap-4">
                  {secondary.map((a) => (
                    <CompactCard key={a.id} article={a} dense />
                  ))}
                </div>
              </section>
            )}

            {remaining.length > 0 && (
              <section>
                <SectionHeading title="Archive" />
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {visible.map((a) => (
                    <ArchiveCard key={a.id} article={a} />
                  ))}
                </div>
                {remaining.length > visibleCount && (
                  <div className="mt-10 flex flex-col items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setVisibleCount((c) => c + PAGE)}
                    >
                      Load more articles
                    </Button>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <span className="brand-gradient h-px w-10" />
      <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        {title}
      </h2>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function formatDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function articleHref(a: NewsArticle) {
  if (a.has_newsletter) return `/newsletter/${a.slug}`;
  // Full body stored locally → on-site article page; else fall back to the
  // legacy external link (pre-import rows only).
  if (a.has_body) return `/news/${a.slug}`;
  return a.external_url || "#";
}

function ArticleLink({
  article,
  children,
  className,
}: {
  article: NewsArticle;
  children: React.ReactNode;
  className?: string;
}) {
  const href = articleHref(article);
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={className}
      aria-label={article.title}
    >
      {children}
    </a>
  );
}

function FeatureCard({ article }: { article: NewsArticle }) {
  return (
    <ArticleLink
      article={article}
      className="group col-span-1 flex flex-col overflow-hidden rounded-xl border bg-card transition-all hover:-translate-y-0.5 hover:shadow-lg lg:col-span-2"
    >
      {/* 16/9 while stacked; on large screens the graphic stretches so the
          card matches the height of the four compact stories beside it.
          Frame size unchanged (P25-b); the picture is shown whole over a
          blurred copy of itself (P27-a). */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-secondary lg:aspect-auto lg:min-h-64 lg:flex-1">
        {article.image_url ? (
          <FramedPicture
            src={article.image_url}
            alt={article.image_alt ?? ""}
            className="transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
            No image
          </div>
        )}
        <div className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-primary-foreground">
          Featured
        </div>
      </div>
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <span className="font-semibold text-primary">
            {article.category || "Insights"}
          </span>
          <span aria-hidden>•</span>
          <time dateTime={article.published_at}>
            {formatDate(article.published_at)}
          </time>
        </div>
        <h3 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight text-foreground transition-colors group-hover:text-primary sm:text-3xl">
          {noWidow(article.title)}
        </h3>
        {article.excerpt && (
          <p className="mt-3 line-clamp-4 text-justify hyphens-auto text-sm leading-relaxed text-muted-foreground sm:text-base">
            {article.excerpt}
          </p>
        )}
        <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary">
          Read the story <span aria-hidden>→</span>
        </div>
      </div>
    </ArticleLink>
  );
}

function CompactCard({ article, dense }: { article: NewsArticle; dense?: boolean }) {
  return (
    <ArticleLink
      article={article}
      className={
        dense
          ? "group flex flex-1 gap-3 overflow-hidden rounded-lg border bg-card p-2.5 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md sm:p-3"
          : "group flex gap-4 overflow-hidden rounded-lg border bg-card p-3 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md sm:gap-5 sm:p-4"
      }
    >
      <div
        className={
          dense
            ? "relative h-full min-h-20 w-24 shrink-0 overflow-hidden rounded-md bg-secondary sm:w-28"
            : "relative h-24 w-28 shrink-0 overflow-hidden rounded-md bg-secondary sm:h-28 sm:w-36"
        }
      >
        {article.image_url ? (
          <FramedPicture
            src={article.image_url}
            alt={article.image_alt ?? ""}
            className="transition-transform duration-500 group-hover:scale-105"
          />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col justify-center">
        <div className={dense ? "text-[10px] uppercase tracking-wider text-muted-foreground" : "text-[11px] uppercase tracking-wider text-muted-foreground"}>
          {formatDate(article.published_at)}
        </div>
        <h3
          className={
            dense
              ? "mt-0.5 line-clamp-2 font-display text-sm font-semibold leading-snug text-foreground transition-colors group-hover:text-primary sm:text-base"
              : "mt-1 line-clamp-2 font-display text-base font-semibold leading-snug text-foreground transition-colors group-hover:text-primary sm:text-lg"
          }
        >
          {noWidow(article.title)}
        </h3>
        {article.excerpt && (
          <p
            className={
              dense
                ? "mt-0.5 line-clamp-1 text-justify hyphens-auto text-xs text-muted-foreground"
                : "mt-1 line-clamp-2 text-justify hyphens-auto text-xs text-muted-foreground sm:text-sm"
            }
          >
            {article.excerpt}
          </p>
        )}
      </div>
    </ArticleLink>
  );
}

function ArchiveCard({ article }: { article: NewsArticle }) {
  return (
    <ArticleLink
      article={article}
      className="group flex h-full flex-col overflow-hidden rounded-lg border bg-card transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-secondary">
        {article.image_url ? (
          <FramedPicture
            src={article.image_url}
            alt={article.image_alt ?? ""}
            className="transition-transform duration-500 group-hover:scale-105"
          />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {formatDate(article.published_at)}
        </div>
        <h3 className="mt-1 line-clamp-3 font-display text-base font-semibold leading-snug text-foreground transition-colors group-hover:text-primary">
          {noWidow(article.title)}
        </h3>
        {article.excerpt && (
          <p className="mt-2 line-clamp-3 text-justify hyphens-auto text-xs text-muted-foreground">
            {article.excerpt}
          </p>
        )}
      </div>
    </ArticleLink>
  );
}
